import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  CreditCard,
  Calculator,
  PieChart,
  BarChart3,
  FileText,
  Eye,
  Trash2,
  Edit,
  Save,
  X,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Bookmark,
  Star,
  Share,
  Info,
  ExternalLink,
  Archive,
  Send,
  Flag,
  Heart,
  ThumbsUp,
  ThumbsDown,
  Upload,
  FileUp,
  FileDown,
  FileCheck,
  FileX,
  Volume2,
  VolumeX,
  Headphones,
  Speaker,
  Radio,
  Music,
  Video,
  Camera,
  Image,
  File,
  Folder,
  FolderOpen,
  HardDrive,
  Cloud,
  Wifi,
  WifiOff,
  Signal,
  Battery,
  BatteryLow,
  Power,
  PowerOff,
  Activity,
  Zap,
  Shield,
  Lock,
  Key,
  Users,
  Building,
  Calendar,
  Phone,
  Mail,
  MapPin,
  CheckSquare,
  Square as SquareIcon,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeft,
  Home,
  Wrench,
  FileBarChart,
  Cog,
  Settings,
  Link,
  Monitor,
  Tablet,
  Smartphone,
  Lightbulb,
  Cpu,
  Network,
  Database,
  Layers,
  User,
  Languages,
  Mic,
  Play,
  Pause,
  Square,
  RefreshCw,
  Copy,
  Download,
  Globe,
  Users2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Target,
  Brain,
  MessageSquare,
  AlertCircle,
  CheckSquare as CheckSquareIcon,
  Receipt,
  Bot,
  Scan,
  Zap as ZapIcon,
  Shield as ShieldIcon,
  LineChart,
  BarChart,
  PieChart as PieChartIcon,
  AreaChart,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ArrowUp,
  ArrowDown,
  Minus,
  Percent,
  Calendar as CalendarIcon,
  Clock as ClockIcon,
  AlertCircle as AlertCircleIcon,
  Info as InfoIcon,
  HelpCircle,
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import { useToast } from '../common/Toast';
import { cashFlowForecastService } from '../../services/cashFlowForecastService';

// Custom Dropdown Component for Forecast Actions
const ForecastActionDropdown = ({
  forecast,
  onView,
  onEdit,
  onGenerate,
  onDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const closeOnScroll = () => setIsOpen(false);
    document.addEventListener('scroll', closeOnScroll, true);

    return () => {
      document.removeEventListener('scroll', closeOnScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const menuHeight = 250;

      setOpenUpward(spaceBelow < menuHeight);
    }
    setIsOpen(!isOpen);
  };

  const handleView = () => {
    setIsOpen(false);
    if (forecast && (forecast.id || forecast._id)) {
      onView(forecast);
    }
  };

  const handleEdit = () => {
    setIsOpen(false);
    if (forecast && (forecast.id || forecast._id)) {
      onEdit(forecast);
    }
  };

  const handleGenerate = () => {
    setIsOpen(false);
    if (forecast && (forecast.id || forecast._id)) {
      onGenerate(forecast);
    }
  };

  const handleDelete = () => {
    setIsOpen(false);
    if (forecast && (forecast.id || forecast._id)) {
      onDelete(forecast);
    }
  };

  return (
    <div
      className="dropdown"
      ref={dropdownRef}
      style={{ position: 'relative', zIndex: 1050 }}
    >
      <button
        ref={buttonRef}
        className="btn btn-outline-secondary btn-sm"
        type="button"
        onClick={handleToggle}
        style={{
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          color: '#3A8DCC',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '32px',
          height: '32px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#E8F1FD';
          e.target.style.borderColor = '#3A8DCC';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#ffffff';
          e.target.style.borderColor = '#e5e7eb';
        }}
      >
        <MoreHorizontal size={16} style={{ color: '#3A8DCC' }} />
      </button>
      {isOpen &&
        (() => {
          const rect = buttonRef.current.getBoundingClientRect();
          const menuMinWidth = 180;
          const left = Math.min(
            rect.left,
            window.innerWidth - menuMinWidth - 16,
          );
          const top = rect.bottom + 8;
          const bottom = window.innerHeight - rect.top + 8;

          const menuStyle = {
            position: 'fixed',
            top: openUpward ? undefined : `${top}px`,
            bottom: openUpward ? `${bottom}px` : undefined,
            left: `${left}px`,
            zIndex: 100000,
            minWidth: `${menuMinWidth}px`,
            boxShadow:
              '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '4px 0',
            backgroundColor: 'white',
            listStyle: 'none',
            margin: 0,
            overflow: 'hidden',
          };

          return createPortal(
            <ul
              className="dropdown-menu dropdown-menu-end show"
              style={menuStyle}
            >
              <li>
                <button
                  onClick={handleView}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                >
                  <Eye
                    size={16}
                    className="mr-2.5 text-gray-500 flex-shrink-0"
                  />
                  View Forecast
                </button>
              </li>

              <li>
                <button
                  onClick={handleEdit}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                >
                  <Edit
                    size={16}
                    className="mr-2.5 text-gray-500 flex-shrink-0"
                  />
                  Edit Forecast
                </button>
              </li>

              <li>
                <button
                  onClick={handleGenerate}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                >
                  <TrendingUp
                    size={16}
                    className="mr-2.5 text-gray-500 flex-shrink-0"
                  />
                  Generate AI Forecast
                </button>
              </li>

              <li className="border-t border-gray-200">
                <button
                  onClick={handleDelete}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 bg-transparent hover:bg-red-50 border-none cursor-pointer transition-colors text-left"
                >
                  <Trash2
                    size={16}
                    className="mr-2.5 text-red-600 flex-shrink-0"
                  />
                  Delete Forecast
                </button>
              </li>
            </ul>,
            document.body,
          );
        })()}
    </div>
  );
};

const CashFlowForecasting = () => {
  const { success, error: showError } = useToast();
  const [activeTab, setActiveTab] = useState('forecasting');
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showScenarioModal, setShowScenarioModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();

  const [cashFlowForecasts, setCashFlowForecasts] = useState([]);
  const [forecastingScenarios, setForecastingScenarios] = useState([]);
  const [selectedForecastId, setSelectedForecastId] = useState('');
  const [forecastAnalytics, setForecastAnalytics] = useState(null);

  const [scenarioFormData, setScenarioFormData] = useState({
    scenarioName: '',
    scenarioType: 'Base Case',
    probability: 50,
    revenueGrowth: 0,
    expenseGrowth: 0,
    paymentTerms: 30,
    collectionRate: 95,
  });

  const stats = {
    totalForecasts: cashFlowForecasts.length,
    totalScenarios: forecastingScenarios.length,
    totalInflow: forecastAnalytics?.totalInflow || 0,
    totalOutflow: forecastAnalytics?.totalOutflow || 0,
    netFlow: forecastAnalytics?.netFlow || 0,
  };
  const selectedForecast = cashFlowForecasts.find(
    (forecast) => forecast._id === selectedForecastId,
  );

  const tabs = [
    { id: 'forecasting', label: 'Cash Flow Forecasts' },
    { id: 'scenarios', label: 'Forecasting Scenarios' },
    { id: 'analytics', label: 'Forecast Analytics' },
    { id: 'settings', label: 'AI Settings' },
  ];

  const formatCurrency = (value, currency = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
      value || 0,
    );

  const loadForecasts = async () => {
    try {
      setLoading(true);
      const response = await cashFlowForecastService.getForecasts();
      if (response.success) {
        const forecasts = response.data.forecasts || [];
        setCashFlowForecasts(forecasts);
        if (!selectedForecastId && forecasts.length) {
          setSelectedForecastId(forecasts[0]._id);
        }
      }
    } catch (error) {
      showError(error.message || 'Failed to load forecasts');
    } finally {
      setLoading(false);
    }
  };

  const loadForecastAnalytics = async (forecastId) => {
    if (!forecastId) {
      setForecastAnalytics(null);
      return;
    }
    try {
      const response =
        await cashFlowForecastService.getForecastAnalytics(forecastId);
      if (response.success) {
        setForecastAnalytics(response.data);
      }
    } catch (error) {
      showError(error.message || 'Failed to load forecast analytics');
    }
  };

  const loadForecastDetails = async (forecastId) => {
    if (!forecastId) {
      setForecastingScenarios([]);
      return;
    }
    try {
      const response = await cashFlowForecastService.getForecast(forecastId);
      if (response.success) {
        const forecast = response.data;
        setForecastingScenarios(forecast.scenarios || []);
      }
    } catch (error) {
      showError(error.message || 'Failed to load forecast details');
    }
  };

  useEffect(() => {
    loadForecasts();
  }, []);

  useEffect(() => {
    loadForecastAnalytics(selectedForecastId);
    loadForecastDetails(selectedForecastId);
  }, [selectedForecastId]);

  // Define columns for Cash Flow Forecasts DataTable
  const forecastColumns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />,
    },
    {
      key: 'forecastName',
      label: 'Forecast Details',
      width: '300px',
      render: (value, row) => (
        <div>
          <div
            style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}
          >
            {value}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.forecastType} • {new Date(row.startDate).toLocaleDateString()}{' '}
            to {new Date(row.endDate).toLocaleDateString()}
          </div>
        </div>
      ),
    },
    {
      key: 'currency',
      label: 'Currency',
      width: '100px',
    },
    {
      key: 'generatedBy',
      label: 'Generated By',
      width: '120px',
    },
    {
      key: 'accuracy',
      label: 'Accuracy',
      width: '120px',
      render: (accuracy) => (
        <span style={{ fontWeight: '500', color: '#1f2937' }}>
          {accuracy != null ? String(Math.round(accuracy * 100)) + '%' : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '80px',
      render: (value, row) => (
        <ForecastActionDropdown
          forecast={row}
          onView={handleView}
          onEdit={handleEdit}
          onGenerate={handleGenerate}
          onDelete={handleDelete}
        />
      ),
    },
  ];

  const scenarioColumns = [
    {
      key: 'scenarioName',
      label: 'Scenario Details',
      width: '300px',
      render: (value, row) => (
        <div>
          <div
            style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}
          >
            {value}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.scenarioType} • Probability {row.probability}%
          </div>
        </div>
      ),
    },
    {
      key: 'probability',
      label: 'Probability',
      width: '120px',
      render: (probability) => (
        <span style={{ fontWeight: '500', color: '#1f2937' }}>
          {probability}%
        </span>
      ),
    },
    {
      key: 'assumptions',
      label: 'Assumptions',
      width: '200px',
      render: (assumptions) => (
        <span style={{ fontSize: '12px', color: '#6b7280' }}>
          Revenue {assumptions?.revenueGrowth ?? 0}%, Expense{' '}
          {assumptions?.expenseGrowth ?? 0}%
        </span>
      ),
    },
    {
      key: 'forecastData',
      label: 'Periods',
      width: '100px',
      render: (forecastData) => (
        <span style={{ fontWeight: '500', color: '#1f2937' }}>
          {forecastData?.length || 0}
        </span>
      ),
    },
  ];

  const getDateInputValue = (value) => {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toISOString().split('T')[0];
  };

  const resetScenarioForm = () => {
    setScenarioFormData({
      scenarioName: '',
      scenarioType: 'Base Case',
      probability: 50,
      revenueGrowth: 0,
      expenseGrowth: 0,
      paymentTerms: 30,
      collectionRate: 95,
    });
  };

  // Handler functions
  const handleView = (item) => {
    if (item && (item._id || item.id)) {
      setSelectedForecastId(item._id || item.id);
      setActiveTab('analytics');
    }
  };

  const handleEdit = (forecast) => {
    if (!forecast) {
      return;
    }
    navigate(`/finance/cashflow/edit/${forecast._id || forecast.id}`);
  };

  const handleGenerate = (forecast) => {
    setSelectedItem(forecast);
    setShowGenerateModal(true);
  };

  const handleDelete = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleRefresh = async () => {
    setLoading(true);
    await loadForecasts();
    await loadForecastAnalytics(selectedForecastId);
    await loadForecastDetails(selectedForecastId);
    setLoading(false);
  };

  const handleHelpClick = () => {
    const helpUrl = '/help/cash-flow-forecasting';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no',
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  const handleNewForecast = () => {
    navigate('/finance/cashflow/create');
  };

  const handleNewScenario = () => {
    if (!selectedForecastId) {
      showError('Select a forecast before creating a scenario');
      return;
    }
    resetScenarioForm();
    setShowScenarioModal(true);
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '',
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)',
    },
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh data',
      disabled: loading,
    },
    ...(activeTab === 'scenarios'
      ? [
          {
            label: 'New Scenario',
            icon: <Plus size={18} />,
            onClick: handleNewScenario,
            variant: 'primary',
            tooltip: 'Create new forecasting scenario',
          },
        ]
      : [
          {
            label: 'New Forecast',
            icon: <Plus size={18} />,
            onClick: handleNewForecast,
            variant: 'primary',
            tooltip: 'Create new cash flow forecast',
          },
        ]),
  ];

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      if (selectedItem?._id) {
        await cashFlowForecastService.deleteForecast(selectedItem._id);
        success('Forecast deleted successfully');
        await loadForecasts();
        if (selectedForecastId === selectedItem._id) {
          setSelectedForecastId('');
          setForecastAnalytics(null);
          setForecastingScenarios([]);
        }
      }
      setShowDeleteModal(false);
      setSelectedItem(null);
    } catch (error) {
      showError(error.message || 'Failed to delete forecast');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleGenerateConfirm = async () => {
    setIsProcessing(true);
    try {
      if (!selectedItem) {
        setShowGenerateModal(false);
        return;
      }
      const payload = {
        forecastName: `${selectedItem.forecastName} (AI)`,
        forecastType: selectedItem.forecastType,
        startDate: selectedItem.startDate,
        endDate: selectedItem.endDate,
        currency: selectedItem.currency,
      };
      await cashFlowForecastService.generateAIForecast(payload);
      success('AI forecast generated successfully');
      await loadForecasts();
      setShowGenerateModal(false);
      setSelectedItem(null);
    } catch (error) {
      showError(error.message || 'Failed to generate forecast');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleScenarioConfirm = async (event) => {
    event.preventDefault();
    setIsProcessing(true);
    try {
      const payload = {
        scenarioName: scenarioFormData.scenarioName.trim(),
        scenarioType: scenarioFormData.scenarioType,
        probability: Number(scenarioFormData.probability),
        assumptions: {
          revenueGrowth: Number(scenarioFormData.revenueGrowth),
          expenseGrowth: Number(scenarioFormData.expenseGrowth),
          paymentTerms: Number(scenarioFormData.paymentTerms),
          collectionRate: Number(scenarioFormData.collectionRate),
        },
      };
      await cashFlowForecastService.createScenario(selectedForecastId, payload);
      success('Scenario created successfully');
      await loadForecastDetails(selectedForecastId);
      setShowScenarioModal(false);
      resetScenarioForm();
    } catch (error) {
      showError(error.message || 'Failed to create scenario');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Cash Flow Forecasting"
        description="AI-powered cash flow forecasting with scenario planning and predictive analytics"
        actions={headerActions}
      />
      <div className="page-container">
        {/* KPI Cards Grid – StatCard reference styling */}
        <div className="grid-modern-4 w-full gap-6 mb-8">
          {/* Total Forecasts */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <ArrowUpRight size={14} />
                {stats.totalForecasts}
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <TrendingUp size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.totalForecasts}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Total Forecasts
                </div>
              </div>
            </div>
          </div>

          {/* Total Scenarios */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <ArrowUpRight size={14} />
                {stats.totalScenarios}
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <Target size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.totalScenarios}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Total Scenarios
                </div>
              </div>
            </div>
          </div>

          {/* Total Inflow */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 text-[11px] font-semibold px-2 py-1 rounded bg-[#E8F5E9] text-[#166534]">
                Inflow
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <AlertTriangle size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {formatCurrency(
                    stats.totalInflow,
                    selectedForecast?.currency,
                  )}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Total Inflow
                </div>
              </div>
            </div>
          </div>

          {/* Net Flow */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div
                className={`absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold ${
                  stats.netFlow < 0 ? 'text-red-600' : 'text-emerald-600'
                }`}
              >
                {stats.netFlow < 0 ? (
                  <ArrowDownRight size={14} />
                ) : (
                  <ArrowUpRight size={14} />
                )}
                {stats.netFlow < 0 ? '' : '+'}
                {formatCurrency(
                  Math.abs(stats.netFlow),
                  selectedForecast?.currency,
                )}
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <DollarSign size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div
                  className={`text-[20px] font-bold ${
                    stats.netFlow < 0
                      ? 'text-red-600'
                      : 'text-[var(--text-primary)]'
                  }`}
                >
                  {formatCurrency(stats.netFlow, selectedForecast?.currency)}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Net Flow
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabbed Navigation */}
        <div style={{ marginBottom: '32px' }}>
          <div className="flex bg-white border-b border-slate-200">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
          flex items-center justify-center gap-2 px-5 py-3 w-full
          text-sm font-medium
          border-b-2 transition-colors duration-200
          ${
            activeTab === tab.id
              ? '!border-b-2 border-[var(--primary-color)] text-[var(--primary-color)] bg-white'
              : 'border-transparent text-slate-500 hover:bg-slate-100'
          }
        `}
              >
                {tab.id === 'forecasting' && (
                  <TrendingUp
                    size={18}
                    className={
                      activeTab === tab.id
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                )}
                {tab.id === 'scenarios' && (
                  <Target
                    size={18}
                    className={
                      activeTab === tab.id
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                )}
                {tab.id === 'analytics' && (
                  <BarChart3
                    size={18}
                    className={
                      activeTab === tab.id
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                )}
                {tab.id === 'settings' && (
                  <Settings
                    size={18}
                    className={
                      activeTab === tab.id
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                )}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {cashFlowForecasts.length > 0 && (
          <div style={{ marginBottom: '24px', maxWidth: '420px' }}>
            <label style={{ display: 'grid', gap: '6px', fontWeight: 500 }}>
              Selected Forecast
              <select
                value={selectedForecastId}
                onChange={(event) => setSelectedForecastId(event.target.value)}
                style={{
                  padding: '8px 10px',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                }}
              >
                {cashFlowForecasts.map((forecast) => (
                  <option key={forecast._id} value={forecast._id}>
                    {forecast.forecastName} • {forecast.forecastType}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {/* Cash Flow Forecasts Tab */}
        {activeTab === 'forecasting' && (
          <div>
            {/* Forecasts DataTable */}
            <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <DataTable
                data={cashFlowForecasts}
                columns={forecastColumns}
                title={`Cash Flow Forecasts (${cashFlowForecasts.length} forecasts)`}
                loading={loading}
                searchable={false}
                sortable={true}
                exportable={true}
                pagination={true}
                pageSize={10}
                showActions={false}
              />
            </div>
          </div>
        )}

        {/* Forecasting Scenarios Tab */}
        {activeTab === 'scenarios' && (
          <div>
            {/* Scenarios DataTable */}
            <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <DataTable
                data={forecastingScenarios}
                columns={scenarioColumns}
                title={`Forecasting Scenarios (${forecastingScenarios.length} scenarios)`}
                loading={loading}
                searchable={false}
                sortable={true}
                exportable={true}
                pagination={true}
                pageSize={10}
                showActions={false}
              />
            </div>
          </div>
        )}

        {/* Other Tabs */}
        {activeTab === 'analytics' && (
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              minHeight: '260px',
            }}
          >
            {forecastAnalytics ? (
              <div style={{ display: 'grid', gap: '16px' }}>
                <div style={{ fontWeight: 600, fontSize: '16px' }}>
                  {forecastAnalytics.forecastName || 'Forecast Analytics'}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '16px',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Total Inflow
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 600 }}>
                      {formatCurrency(
                        forecastAnalytics.totalInflow,
                        selectedForecast?.currency,
                      )}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Total Outflow
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 600 }}>
                      {formatCurrency(
                        forecastAnalytics.totalOutflow,
                        selectedForecast?.currency,
                      )}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Net Flow
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 600 }}>
                      {formatCurrency(
                        forecastAnalytics.netFlow,
                        selectedForecast?.currency,
                      )}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Periods
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 600 }}>
                      {forecastAnalytics.periods}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ color: '#6b7280' }}>
                Select a forecast to view analytics.
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              minHeight: '260px',
            }}
          >
            {selectedForecast ? (
              <div style={{ display: 'grid', gap: '12px' }}>
                <div style={{ fontWeight: 600, fontSize: '16px' }}>
                  Forecast Settings
                </div>
                <div style={{ color: '#4b5563' }}>
                  Generated By: {selectedForecast.generatedBy}
                </div>
                <div style={{ color: '#4b5563' }}>
                  Accuracy:{' '}
                  {selectedForecast.accuracy != null
                    ? `${Math.round(selectedForecast.accuracy * 100)}%`
                    : 'Not available'}
                </div>
                <div style={{ color: '#4b5563' }}>
                  Assumptions:{' '}
                  {Array.isArray(selectedForecast.assumptions) &&
                  selectedForecast.assumptions.length
                    ? selectedForecast.assumptions.join(', ')
                    : 'No assumptions recorded'}
                </div>
              </div>
            ) : (
              <div style={{ color: '#6b7280' }}>
                Select a forecast to review AI settings and assumptions.
              </div>
            )}
          </div>
        )}

        {showScenarioModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1050,
              padding: '20px',
            }}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                setShowScenarioModal(false);
              }
            }}
          >
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '24px',
                width: '100%',
                maxWidth: '520px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              }}
            >
              <h3 style={{ marginTop: 0, marginBottom: '16px' }}>
                Create Scenario
              </h3>
              <form onSubmit={handleScenarioConfirm}>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Scenario Name
                    <input
                      type="text"
                      value={scenarioFormData.scenarioName}
                      onChange={(event) =>
                        setScenarioFormData({
                          ...scenarioFormData,
                          scenarioName: event.target.value,
                        })
                      }
                      required
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Scenario Type
                    <select
                      value={scenarioFormData.scenarioType}
                      onChange={(event) =>
                        setScenarioFormData({
                          ...scenarioFormData,
                          scenarioType: event.target.value,
                        })
                      }
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      <option value="Best Case">Best Case</option>
                      <option value="Base Case">Base Case</option>
                      <option value="Worst Case">Worst Case</option>
                      <option value="Custom">Custom</option>
                    </select>
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Probability (%)
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={scenarioFormData.probability}
                      onChange={(event) =>
                        setScenarioFormData({
                          ...scenarioFormData,
                          probability: event.target.value,
                        })
                      }
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Revenue Growth (%)
                    <input
                      type="number"
                      value={scenarioFormData.revenueGrowth}
                      onChange={(event) =>
                        setScenarioFormData({
                          ...scenarioFormData,
                          revenueGrowth: event.target.value,
                        })
                      }
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Expense Growth (%)
                    <input
                      type="number"
                      value={scenarioFormData.expenseGrowth}
                      onChange={(event) =>
                        setScenarioFormData({
                          ...scenarioFormData,
                          expenseGrowth: event.target.value,
                        })
                      }
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Payment Terms (days)
                    <input
                      type="number"
                      value={scenarioFormData.paymentTerms}
                      onChange={(event) =>
                        setScenarioFormData({
                          ...scenarioFormData,
                          paymentTerms: event.target.value,
                        })
                      }
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Collection Rate (%)
                    <input
                      type="number"
                      value={scenarioFormData.collectionRate}
                      onChange={(event) =>
                        setScenarioFormData({
                          ...scenarioFormData,
                          collectionRate: event.target.value,
                        })
                      }
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end',
                    marginTop: '20px',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setShowScenarioModal(false)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#ffffff',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isProcessing}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: 'var(--primary-color)',
                      color: '#ffffff',
                    }}
                  >
                    Create Scenario
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Forecast"
          message={`Are you sure you want to delete "${selectedItem?.forecastName}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
        />

        {/* Generate Forecast Modal */}
        <ConfirmationModal
          isOpen={showGenerateModal}
          onClose={() => setShowGenerateModal(false)}
          onConfirm={handleGenerateConfirm}
          title="Generate Forecast"
          message={`Generate AI forecast using "${selectedItem?.forecastName}" parameters? This will create a new AI-generated forecast.`}
          confirmText="Generate"
          cancelText="Cancel"
          type="success"
          isLoading={isProcessing}
        />
      </div>
    </>
  );
};

export default CashFlowForecasting;